import React, { useState } from 'react';
import { Input, Upload, Button, message, Space } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useLanguage } from '../../../contexts/LanguageContext';

const ImageEditor = ({ formData, onFormChange }) => {
  const { t } = useLanguage();
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (file) => {
    setUploading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);

      const token = localStorage.getItem('token');
      if (!token) {
        message.error(`❌ ${t('eformDesigner.uploadFailed')}: ${t('eformDesigner.upload.authTokenNotFound')}`);
        setUploading(false);
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      // 上傳圖片到 Documents 目錄
      const response = await fetch('/api/FormsUpload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataObj,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Invalid response format. Expected JSON, got: ${contentType || 'unknown'}. Response: ${text.substring(0, 100)}`);
      }

      const result = await response.json();

      if (result.success) {
        // 使用 Documents 目錄的路徑
        const imageUrl = `/Uploads/FormsFiles/Documents/${result.fileName}`;
        message.success(`✅ ${t('eformDesigner.imageUploadSuccess')}`);
        onFormChange('src', imageUrl);
      } else {
        message.error(`❌ ${t('eformDesigner.uploadFailed')}: ${result.error || t('eformDesigner.unknownError')}`);
      }
    } catch (error) {
      console.error('❌ 上傳錯誤:', error);
      if (error.name === 'AbortError') {
        message.error(`❌ ${t('eformDesigner.requestTimeoutImageUpload')}`);
      } else if (error.message) {
        message.error(`❌ ${t('eformDesigner.uploadFailed')}: ${error.message}`);
      } else {
        message.error(`❌ ${t('eformDesigner.uploadFailed')}: ${error.toString()}`);
      }
    } finally {
      setUploading(false);
    }
  };

  const uploadProps = {
    name: 'file',
    accept: '.jpg,.jpeg,.png,.gif,.bmp,.webp',
    beforeUpload: (file) => {
      handleImageUpload(file);
      return false; // 阻止自動上傳
    },
    showUploadList: false,
    disabled: uploading
  };

  return (
    <div>
      <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>{t('eformDesigner.editImage')}</h3>
      
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          {t('eformDesigner.imageUrl')}:
        </label>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            value={formData.src || ''}
            onChange={(e) => onFormChange('src', e.target.value)}
            placeholder={t('eformDesigner.pleaseEnterImageUrl')}
          />
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />} loading={uploading} disabled={uploading} style={{ width: '100%' }}>
              {uploading ? t('eformDesigner.uploadingImage') : t('eformDesigner.uploadImage')}
            </Button>
          </Upload>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {t('eformDesigner.imageUploadHint')}
          </div>
        </Space>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          {t('eformDesigner.altText')}:
        </label>
        <Input
          value={formData.alt || ''}
          onChange={(e) => onFormChange('alt', e.target.value)}
          placeholder={t('eformDesigner.pleaseEnterAltText')}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          {t('eformDesigner.imageWidth')}:
        </label>
        <Input
          type="text"
          value={formData.width || ''}
          onChange={(e) => onFormChange('width', e.target.value)}
          placeholder={t('eformDesigner.pleaseEnterWidth')}
        />
        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
          {t('eformDesigner.imageWidthHint')}
        </div>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          {t('eformDesigner.imageHeight')}:
        </label>
        <Input
          type="text"
          value={formData.height || ''}
          onChange={(e) => onFormChange('height', e.target.value)}
          placeholder={t('eformDesigner.pleaseEnterHeight')}
        />
        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
          {t('eformDesigner.imageHeightHint')}
        </div>
      </div>

      {formData.src && (
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            {t('eformDesigner.imagePreview')}:
          </label>
          <img 
            src={formData.src} 
            alt={formData.alt || 'Preview'} 
            style={{ 
              maxWidth: '100%', 
              height: 'auto', 
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '4px'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ImageEditor;
